import type { AudioPlayerState, Track, RepeatMode, Status, PlaybackRate } from './types';

export type AudioAction =
  | { type: 'SET_TRACK'; payload: Track }
  | { type: 'SET_QUEUE'; payload: { tracks: Track[]; startIndex: number } }
  | { type: 'PLAY' }
  | { type: 'PAUSE' }
  | { type: 'SEEK'; payload: number }
  | { type: 'UPDATE_TIME'; payload: number }
  | { type: 'UPDATE_DURATION'; payload: number }
  | { type: 'SET_PLAYBACK_RATE'; payload: PlaybackRate }
  | { type: 'SET_REPEAT_MODE'; payload: RepeatMode }
  | { type: 'NEXT_TRACK' }
  | { type: 'PREVIOUS_TRACK' }
  | { type: 'SET_STATUS'; payload: Status }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_QUEUE' }
  | { type: 'SET_AB_MARKERS'; payload: [number | undefined, number | undefined] }
  | { type: 'SET_ACTIVE_LYRIC_LINE'; payload: number | undefined };

export const initialState: AudioPlayerState = {
  currentTrack: null,
  queue: [],
  queueIndex: -1,
  currentTimeMs: 0,
  durationMs: 0,
  playbackRate: 1.0,
  isPlaying: false,
  status: 'idle',
  repeatMode: 'off',
  error: null,
};

export function audioReducer(state: AudioPlayerState, action: AudioAction): AudioPlayerState {
  switch (action.type) {
    case 'SET_TRACK': {
      const idx = state.queue.findIndex((t) => t.id === action.payload.id);
      return {
        ...state,
        currentTrack: action.payload,
        durationMs: action.payload.durationMs,
        currentTimeMs: 0,
        status: 'loading',
        error: null,
        queueIndex: idx >= 0 ? idx : state.queueIndex,
      };
    }
    case 'SET_QUEUE': {
      const startIndex = Math.max(0, Math.min(action.payload.startIndex, action.payload.tracks.length - 1));
      const track = action.payload.tracks[startIndex] ?? null;
      return {
        ...state,
        queue: action.payload.tracks,
        queueIndex: startIndex,
        currentTrack: track,
        durationMs: track?.durationMs ?? 0,
        currentTimeMs: 0,
        status: track ? 'loading' : 'idle',
        error: null,
      };
    }
    case 'PLAY':
      return { ...state, isPlaying: true, status: state.status === 'error' ? state.status : 'playing' };
    case 'PAUSE':
      return { ...state, isPlaying: false, status: state.status === 'error' ? state.status : 'paused' };
    case 'SEEK':
    case 'UPDATE_TIME':
      return { ...state, currentTimeMs: action.payload };
    case 'UPDATE_DURATION':
      return { ...state, durationMs: action.payload };
    case 'SET_PLAYBACK_RATE':
      return { ...state, playbackRate: action.payload };
    case 'SET_REPEAT_MODE':
      return { ...state, repeatMode: action.payload };
    case 'NEXT_TRACK': {
      if (state.queue.length === 0) return state;
      const nextIdx = (state.queueIndex + 1) % state.queue.length;
      const nextTrack = state.queue[nextIdx];
      return { ...state, queueIndex: nextIdx, currentTrack: nextTrack, durationMs: nextTrack.durationMs, currentTimeMs: 0, status: 'loading' };
    }
    case 'PREVIOUS_TRACK': {
      if (state.queue.length === 0) return state;
      const prevIdx = state.queueIndex === 0 ? state.queue.length - 1 : state.queueIndex - 1;
      const prevTrack = state.queue[prevIdx];
      return { ...state, queueIndex: prevIdx, currentTrack: prevTrack, durationMs: prevTrack.durationMs, currentTimeMs: 0, status: 'loading' };
    }
    case 'SET_STATUS':
      return { ...state, status: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, status: action.payload ? 'error' : state.status };
    case 'CLEAR_QUEUE':
      return { ...initialState };
    case 'SET_AB_MARKERS':
      return { ...state, abMarkerStart: action.payload[0], abMarkerEnd: action.payload[1] };
    case 'SET_ACTIVE_LYRIC_LINE':
      return { ...state, activeLyricLineIndex: action.payload };
    default:
      return state;
  }
}
